"use client";

import { Tool } from "@/types";
import { usePathname } from "next/navigation";
import { WordCounter } from "@/components/tools/text/WordCounter";
import { ImageResizer } from "@/components/tools/image/ImageResizer";
import dynamic from "next/dynamic";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getLocalizedTool } from "@/lib/tool-localization";
import { getToolUiCopy } from "@/lib/tool-ui-copy";

function LocalizedLoading({ kind }: { kind: "pdfTool" | "pdfEditor" | "vectorizer" }) {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);

  const label =
    kind === "pdfEditor"
      ? ui.loadingPdfEditor
      : kind === "vectorizer"
        ? ui.loadingVectorizer
        : ui.loadingPdfTool;

  return <p>{label}</p>;
}

// Dynamically import PDF tools
const PdfToJpg = dynamic(() => import("@/components/tools/pdf/PdfToJpg").then(mod => mod.PdfToJpg), {
  ssr: false,
  loading: () => <LocalizedLoading kind="pdfTool" />
});
const PdfToPng = dynamic(() => import("@/components/tools/pdf/PdfToPng").then(mod => mod.PdfToPng), {
  ssr: false,
  loading: () => <LocalizedLoading kind="pdfTool" />
});
const MergePdf = dynamic(() => import("@/components/tools/pdf/MergePdf").then(mod => mod.MergePdf));
const SplitPdf = dynamic(() => import("@/components/tools/pdf/SplitPdf").then(mod => mod.SplitPdf));
const CompressPdf = dynamic(() => import("@/components/tools/pdf/CompressPdf").then(mod => mod.CompressPdf), { ssr: false });
const JpgToPdf = dynamic(() => import("@/components/tools/pdf/JpgToPdf").then(mod => mod.JpgToPdf));
const RotatePdf = dynamic(() => import("@/components/tools/pdf/RotatePdf").then(mod => mod.RotatePdf));
const AddWatermark = dynamic(() => import("@/components/tools/pdf/AddWatermark").then(mod => mod.AddWatermark));
const PdfEditor = dynamic(() => import("@/components/tools/pdf/PdfEditor").then(mod => mod.PdfEditor), {
  ssr: false,
  loading: () => <LocalizedLoading kind="pdfEditor" />
});
const SignPdf = dynamic(() => import("@/components/tools/pdf/SignPdf").then(mod => mod.SignPdf));

// Office conversions
const PdfToWord = dynamic(() => import("@/components/tools/pdf/PdfToWord").then(mod => mod.PdfToWord));
const WordToPdf = dynamic(() => import("@/components/tools/pdf/WordToPdf").then(mod => mod.WordToPdf));
const PdfToExcel = dynamic(() => import("@/components/tools/pdf/PdfToExcel").then(mod => mod.PdfToExcel));
const ExcelToPdf = dynamic(() => import("@/components/tools/pdf/ExcelToPdf").then(mod => mod.ExcelToPdf));
const PdfToPowerpoint = dynamic(() => import("@/components/tools/pdf/PdfToPowerpoint").then(mod => mod.PdfToPowerpoint));

// Dynamically import text tools
const CaseConverter = dynamic(() => import("@/components/tools/text/CaseConverter").then(mod => mod.CaseConverter));
const LoremIpsum = dynamic(() => import("@/components/tools/text/LoremIpsum").then(mod => mod.LoremIpsum));
const JsonFormatter = dynamic(() => import("@/components/tools/text/JsonFormatter").then(mod => mod.JsonFormatter));
const DiffChecker = dynamic(() => import("@/components/tools/text/DiffChecker").then(mod => mod.DiffChecker));
const MarkdownEditor = dynamic(() => import("@/components/tools/text/MarkdownEditor").then(mod => mod.MarkdownEditor));

// Dynamically import image tools
const ImageCompressor = dynamic(() => import("@/components/tools/image/ImageCompressor").then(mod => mod.ImageCompressor));
const ImageConverter = dynamic(() => import("@/components/tools/image/ImageConverter").then(mod => mod.ImageConverter));
const ImageCropper = dynamic(() => import("@/components/tools/image/ImageCropper").then(mod => mod.ImageCropper));
const RemoveBackground = dynamic(() => import("@/components/tools/image/RemoveBackground").then(mod => mod.RemoveBackground));
const ImageToVectorTool = dynamic(() => import("@/components/tools/image-to-vector/ImageToVectorTool").then(mod => mod.default), {
  ssr: false,
  loading: () => <LocalizedLoading kind="vectorizer" />
});

// Dynamically import dev tools
const QrGenerator = dynamic(() => import("@/components/tools/dev/QrGenerator").then(mod => mod.QrGenerator));
const PasswordGenerator = dynamic(() => import("@/components/tools/dev/PasswordGenerator").then(mod => mod.PasswordGenerator));
const UuidGenerator = dynamic(() => import("@/components/tools/dev/UuidGenerator").then(mod => mod.UuidGenerator));
const Base64Encoder = dynamic(() => import("@/components/tools/dev/Base64Encoder").then(mod => mod.Base64Encoder));
const UrlEncoder = dynamic(() => import("@/components/tools/dev/UrlEncoder").then(mod => mod.UrlEncoder));
const ColorPicker = dynamic(() => import("@/components/tools/dev/ColorPicker").then(mod => mod.ColorPicker));

interface ToolRendererProps {
  tool: Tool;
}

export function ToolRenderer({ tool }: ToolRendererProps) {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const localizedTool = getLocalizedTool(tool, locale);
  const ui = getToolUiCopy(locale);
  // Text Tools (6/6)
  if (tool.id === 'word-counter') return <WordCounter />;
  if (tool.id === 'case-converter') return <CaseConverter />;
  if (tool.id === 'lorem-ipsum') return <LoremIpsum />;
  if (tool.id === 'json-formatter') return <JsonFormatter />;
  if (tool.id === 'diff-checker') return <DiffChecker />;
  if (tool.id === 'markdown-editor') return <MarkdownEditor />;
  
  // Image Tools (8/8)
  if (tool.id === 'image-resizer') return <ImageResizer />;
  if (tool.id === 'image-compressor') return <ImageCompressor />;
  if (tool.id === 'image-converter') return <ImageConverter defaultSource="jpg" defaultTarget="png" />;
  if (tool.id === 'jpg-to-png') return <ImageConverter defaultSource="jpg" defaultTarget="png" />;
  if (tool.id === 'png-to-jpg') return <ImageConverter defaultSource="png" defaultTarget="jpg" />;
  if (tool.id === 'webp-to-jpg') return <ImageConverter defaultSource="webp" defaultTarget="jpg" />;
  if (tool.id === 'webp-to-png') return <ImageConverter defaultSource="webp" defaultTarget="png" />;
  if (tool.id === 'png-to-webp') return <ImageConverter defaultSource="png" defaultTarget="webp" />;
  if (tool.id === 'jpg-to-webp') return <ImageConverter defaultSource="jpg" defaultTarget="webp" />;
  if (tool.id === 'remove-background') return <RemoveBackground />;
  if (tool.id === 'image-to-vector') return <ImageToVectorTool />;
  
  // PDF Tools (15/15) - ALL COMPLETE!
  if (tool.id === 'pdf-to-jpg') return <PdfToJpg />;
  if (tool.id === 'pdf-to-png') return <PdfToPng />;
  if (tool.id === 'pdf-to-word') return <PdfToWord />;
  if (tool.id === 'pdf-to-excel') return <PdfToExcel />;
  if (tool.id === 'pdf-to-powerpoint') return <PdfToPowerpoint />;
  if (tool.id === 'merge-pdf') return <MergePdf />;
  if (tool.id === 'split-pdf') return <SplitPdf />;
  if (tool.id === 'compress-pdf') return <CompressPdf />;
  if (tool.id === 'jpg-to-pdf') return <JpgToPdf />;
  if (tool.id === 'word-to-pdf') return <WordToPdf />;
  if (tool.id === 'excel-to-pdf') return <ExcelToPdf />;
  if (tool.id === 'rotate-pdf') return <RotatePdf />;
  if (tool.id === 'add-watermark') return <AddWatermark />;
  if (tool.id === 'edit-pdf') return <PdfEditor />;
  if (tool.id === 'sign-pdf') return <SignPdf />;
  
  // Dev Tools (9/9)
  if (tool.id === 'qr-generator') return <QrGenerator />;
  if (tool.id === 'password-generator') return <PasswordGenerator />;
  if (tool.id === 'uuid-generator') return <UuidGenerator />;
  if (tool.id === 'base64-encode') return <Base64Encoder />;
  if (tool.id === 'base64-decode') return <Base64Encoder />;
  if (tool.id === 'url-encoder') return <UrlEncoder />;
  if (tool.id === 'url-decoder') return <UrlEncoder />;
  if (tool.id === 'color-picker') return <ColorPicker />;
  if (tool.id === 'hash-generator') return <PasswordGenerator />; // Reuse password generator

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 rounded-full bg-primary/10 p-4">
        <span className="text-2xl">🛠️</span>
      </div>
      <h3 className="text-xl font-semibold">{ui.toolComingSoonTitle}</h3>
      <p className="mt-2 max-w-md text-muted-foreground">
        {ui.toolComingSoonBodyPrefix} <strong>{localizedTool.name}</strong>. {ui.toolComingSoonBodySuffix}
      </p>
    </div>
  );
}
